import React from "react";
import { Box, Chip, Stack, Typography, alpha, useTheme } from "@mui/material";
import NightsStayIcon from "@mui/icons-material/NightsStay";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import type { RotaOverlay } from "../data/types";

interface RotaOverlayPanelProps {
  rota: RotaOverlay;
}

const field = (label: string, value: React.ReactNode) => (
  <Stack spacing={0.25} key={label}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {value}
    </Typography>
  </Stack>
);

/** Rota / scheduling overlay shown alongside operational cohort detail. */
export const RotaOverlayPanel: React.FC<RotaOverlayPanelProps> = ({ rota }) => {
  const theme = useTheme();
  const shortRest = rota.restPeriodHours < 11;

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        {rota.isNightShift ? (
          <NightsStayIcon fontSize="small" color="info" />
        ) : (
          <WbSunnyIcon fontSize="small" color="warning" />
        )}
        <Typography variant="subtitle2" fontWeight={700}>
          Rota overlay
        </Typography>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)" },
          gap: 2,
        }}
      >
        {field("Shift type", <span style={{ textTransform: "capitalize" }}>{rota.shiftType}</span>)}
        {field(
          "Start time",
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
            <AccessTimeIcon sx={{ fontSize: 14 }} />
            {rota.startTime}
          </Box>,
        )}
        {field("Day / night", rota.isNightShift ? "Night" : "Day")}
        {field(
          "Rest period",
          <Box
            component="span"
            sx={{ color: shortRest ? theme.palette.error.main : "inherit" }}
          >
            {rota.restPeriodHours}h{shortRest ? " ⚠" : ""}
          </Box>,
        )}
        {field("Consecutive shifts", rota.consecutiveShifts)}
        {field(
          "Overtime",
          rota.overtimeFlag ? (
            <Chip
              label="Flagged"
              size="small"
              sx={{
                height: 20,
                fontSize: 11,
                fontWeight: 700,
                color: theme.palette.warning.main,
                bgcolor: alpha(theme.palette.warning.main, 0.14),
              }}
            />
          ) : (
            "None"
          ),
        )}
      </Box>
    </Box>
  );
};
